import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { IrisBrand } from "@/components/common/IrisBrand";
import { post } from "@/lib/api";

export default function ForceChangePassword() {
  const { me, logout, refetchMe } = useApp();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const changePassword = useMutation({
    mutationFn: (body) => post("/auth/change-password", body),
    onSuccess: () => refetchMe(),
    onError: (err) => setError(err?.response?.data?.detail || "Couldn't change your password"),
  });

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    changePassword.mutate({ current_password: currentPassword, new_password: newPassword });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <IrisBrand size="lg" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="font-display text-lg font-semibold text-foreground">Set a new password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Hi {me?.name || "there"} — you're signed in with a temporary password. Set your own before continuing.
          </p>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="fcp-current">Temporary password</Label>
              <PasswordInput id="fcp-current" autoComplete="current-password" className="mt-1"
                data-testid="fcp-current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="fcp-new">New password</Label>
              <PasswordInput id="fcp-new" autoComplete="new-password" className="mt-1"
                data-testid="fcp-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="fcp-confirm">Confirm new password</Label>
              <PasswordInput id="fcp-confirm" autoComplete="new-password" className="mt-1"
                data-testid="fcp-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <Button type="submit" className="w-full" data-testid="fcp-submit" disabled={changePassword.isPending}>
              {changePassword.isPending ? "Saving..." : "Save and continue"}
            </Button>

            <div className="text-center">
              <button type="button" onClick={logout} className="text-sm text-muted-foreground hover:underline">
                Sign out instead
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
